// pages/JoinPage.jsx
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Checkbox, FormControlLabel, Grid, Link, Stack, Typography } from "@mui/material";
import PageShell from "../components/PageShell";
import { getIconEl } from "../components/utils";
import {
  createJoinConsentRecord,
  getJoinMessages,
  JOIN_CONSENT_STORAGE_KEY,
} from "../content/joinMessages";

/**
 * Join page (extracted from App.jsx)
 * Props: { data }
 */
export default function JoinPage({ data }) {
  const messages = getJoinMessages(document.documentElement.lang || undefined);
  const [consents, setConsents] = useState({ rules: false, legal: false, profile: false });
  const intro = data?.join?.intro ?? data?.joinIntro ?? "Registration form, Discord link, briefing pack, and tech checks.";
  const discord = data?.join?.discord;
  const reqs = data?.join?.requirements ?? [];
  const tech = data?.join?.techChecks ?? [];
  const infos = data?.join?.information ?? [];
  const allConsentsAccepted = Object.values(consents).every(Boolean);

  const setConsent = (name) => (event) => {
    setConsents((current) => ({ ...current, [name]: event.target.checked }));
  };

  const recordConsent = () => {
    if (!allConsentsAccepted) return;
    window.localStorage.setItem(
      JOIN_CONSENT_STORAGE_KEY,
      JSON.stringify(createJoinConsentRecord(consents)),
    );
  };

  return (
    <PageShell title="Join ARL" bannerImage="join.png">
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>Start Your Journey</Typography>
              <Typography sx={{ mb: 2 }}>{intro}</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {messages.consentHeading}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {messages.consentHelp}
                </Typography>
                <Stack>
                  <FormControlLabel
                    control={<Checkbox checked={consents.rules} onChange={setConsent("rules")} />}
                    label={
                      <Typography variant="body2">
                        {messages.rulesPrefix}
                        <Link component={RouterLink} to="/rules">{messages.rulesLink}</Link>.
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={<Checkbox checked={consents.legal} onChange={setConsent("legal")} />}
                    label={
                      <Typography variant="body2">
                        {messages.termsPrefix}
                        <Link component={RouterLink} to="/terms">{messages.termsLink}</Link>
                        {messages.privacyConnector}
                        <Link component={RouterLink} to="/privacy">{messages.privacyLink}</Link>.
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={<Checkbox checked={consents.profile} onChange={setConsent("profile")} />}
                    label={<Typography variant="body2">{messages.profileDisclosure}</Typography>}
                  />
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {discord && (
                  <Button
                    variant="contained"
                    color="secondary"
                    href={discord}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={getIconEl("Discord")}
                    size="large"
                    disabled={!allConsentsAccepted}
                    onClick={recordConsent}
                  >
                    {messages.joinDiscord}
                  </Button>
                )}
              </Stack>
            </Box>

            {infos.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                  Information
                </Typography>
                <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                  {infos.map((r, i) => (
                    <Typography key={i} component="li" variant="body2" color="text.secondary">
                      {r}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {reqs.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                  Requirements
                </Typography>
                <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                  {reqs.map((r, i) => (
                    <Typography key={i} component="li" variant="body2" color="text.secondary">
                      {r}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {tech.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                  Tech Checks
                </Typography>
                <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                  {tech.map((t, i) => (
                    <Typography key={i} component="li" variant="body2" color="text.secondary">
                      {t}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            component="img"
            src="join2.png"
            alt="Join the Race"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 3,
              minHeight: 400,
            }}
          />
        </Grid>
      </Grid>
    </PageShell>
  );
};
